import { z } from 'zod';

import { AUDIENCE_KINDS } from '../domain/audience.js';
import { FIELD_SETS } from '../domain/enums.js';
import { slugSchema, uuidSchema } from './common.js';

export const audienceRuleInputSchema = z
  .object({
    kind: z.enum(AUDIENCE_KINDS),
    departmentId: uuidSchema.nullable().default(null),
    jobRoleId: uuidSchema.nullable().default(null),
    userId: uuidSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, ctx) => {
    const expected = {
      department: value.departmentId,
      position: value.jobRoleId,
      person: value.userId,
    };

    const set = Object.entries(expected).filter(([, id]) => id !== null).map(([key]) => key);

    if (set.length !== 1 || set[0] !== value.kind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `A ${value.kind} rule must carry exactly that one target.`,
      });
    }
  });

export type AudienceRuleInput = z.infer<typeof audienceRuleInputSchema>;

export const routingTargetInputSchema = z
  .object({
    kind: z.enum(AUDIENCE_KINDS),
    departmentId: uuidSchema.nullable().default(null),
    jobRoleId: uuidSchema.nullable().default(null),
    userId: uuidSchema.nullable().default(null),
  })
  .strict()
  .superRefine((value, ctx) => {
    const expected = {
      department: value.departmentId,
      position: value.jobRoleId,
      person: value.userId,
    };

    const set = Object.entries(expected).filter(([, id]) => id !== null).map(([key]) => key);

    if (set.length !== 1 || set[0] !== value.kind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `A ${value.kind} target must carry exactly that one reference.`,
      });
    }
  });

export type RoutingTargetInput = z.infer<typeof routingTargetInputSchema>;

const audienceListSchema = z.array(audienceRuleInputSchema).max(200);

const boardNameSchema = z.string().trim().min(2).max(60);
const boardDescriptionSchema = z.string().trim().max(500);

export const createBoardSchema = z
  .object({
    slug: slugSchema,
    name: boardNameSchema,
    description: boardDescriptionSchema.nullable().default(null),
    fieldSet: z.enum(FIELD_SETS).default('basic'),
    assigneeMayBeCreator: z.boolean().default(true),
    requiresProjectAuthorisation: z.boolean().default(false),
    allowManualStatusChange: z.boolean().default(true),
    viewAudience: audienceListSchema.default([]),
    raiseAudience: audienceListSchema.default([]),
    intakeTarget: routingTargetInputSchema.nullable().default(null),
  })
  .strict();

export const updateBoardSchema = z
  .object({
    name: boardNameSchema.optional(),
    description: boardDescriptionSchema.nullable().optional(),
    assigneeMayBeCreator: z.boolean().optional(),
    requiresProjectAuthorisation: z.boolean().optional(),
    allowManualStatusChange: z.boolean().optional(),
    viewAudience: audienceListSchema.optional(),
    raiseAudience: audienceListSchema.optional(),
    intakeTarget: routingTargetInputSchema.nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update.');

export const reorderBoardsSchema = z
  .object({ boardIds: z.array(uuidSchema).min(1).max(50) })
  .strict();

export type CreateBoardInput = z.infer<typeof createBoardSchema>;
export type UpdateBoardInput = z.infer<typeof updateBoardSchema>;
export type ReorderBoardsInput = z.infer<typeof reorderBoardsSchema>;

export const createResponsibilitySchema = z
  .object({
    slug: slugSchema,
    name: boardNameSchema,
    description: boardDescriptionSchema.nullable().default(null),
    departmentId: uuidSchema,
    holderId: uuidSchema.nullable().default(null),
  })
  .strict();

export const updateResponsibilitySchema = z
  .object({
    name: boardNameSchema.optional(),
    description: boardDescriptionSchema.nullable().optional(),
    departmentId: uuidSchema.optional(),
    holderId: uuidSchema.nullable().optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update.');

export type CreateResponsibilityInput = z.infer<typeof createResponsibilitySchema>;
export type UpdateResponsibilityInput = z.infer<typeof updateResponsibilitySchema>;
